'use client';

import { useRef, type ReactNode } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { motion, useScroll, useTransform } from 'framer-motion';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import type { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';

import cardDots from '@/public/fabio-affiliate/card-dots.png';

function IntroItem({
  index,
  children,
}: {
  index: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLLIElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['center end', 'center start'],
  });
  const filter = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    ['blur(12px)', 'blur(0px)', 'blur(0px)'],
  );

  return (
    <motion.li
      ref={ref}
      key={`intro-item-${index}`}
      style={{ filter, willChange: 'filter' }}
      className="flex relative flex-col justify-between bg-white border gap-[1.5rem] border-[#2F2F2F]/10 p-[3rem] h-[25.5rem]"
    >
      {children}
    </motion.li>
  );
}

const DotsIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="3" height="3" fill="#6DA991" />
    <rect x="7" width="3" height="3" fill="#6DA991" />
    <rect x="14" width="3" height="3" fill="#6DA991" />
    <rect y="7" width="3" height="3" fill="#6DA991" />
    <rect x="7" y="7" width="3" height="3" fill="#6DA991" />
    <rect x="14" y="7" width="3" height="3" fill="#6DA991" />
    <rect y="14" width="3" height="3" fill="#6DA991" />
    <rect x="7" y="14" width="3" height="3" fill="#6DA991" />
    <rect x="14" y="14" width="3" height="3" fill="#6DA991" />
  </svg>
);

export default function IntroSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  if (
    !data?.introChip &&
    !data?.introHeading &&
    !data?.introText &&
    !data?.introItems?.length &&
    !data?.introCTA
  ) {
    return null;
  }

  return (
    <section className="lg:px-0 pt-[10rem] lg:pt-[15rem] px-[2rem] lg:pb-[15rem]">
      <div className="mx-auto w-[min(160rem,100%)]  gap-[6rem]  flex lg:flex-row flex-col">
        <div className="flex flex-col gap-[2rem] w-[min(68.375rem,100%)] h-fit lg:sticky lg:top-[10rem]">
          {data?.introChip && (
            <span className="font-[400] leading-[150%] text-black text-[2rem] font-blacker italic w-fit mx-auto lg:mx-0">
              {data.introChip}
            </span>
          )}
          {data?.introHeading && (
            <div className="richtext lg:richtext-start lg:[&_br]:hidden prose-p:text-[2rem] prose-p:font-[400] prose-p:text-black/70 prose-em:font-blacker prose-em:font-[400] prose-headings:text-black prose-headings:font-geist  prose-headings:leading-[110%] prose-headings:tracking-[-0.25rem] lg:prose-headings:tracking-[-0.4rem] prose-headings:font-[500]">
              <PortableText
                value={data.introHeading}
                components={richTextComponent}
              />
            </div>
          )}
          {data?.introCTA && (
            <div className="hidden lg:block mt-[2rem] lg:w-fit w-full">
              <CTAButton data={data.introCTA} />
            </div>
          )}
        </div>

        {data?.introItems?.length > 0 && (
          <div className="w-[min(80rem,100%)] flex items-end justify-end relative overflow-y-clip ">
            <div className="absolute top-0 left-0 h-full w-[2.5rem]">
              <div className="relative w-full h-full pt-[1rem]">
                <div className="absolute top-0 left-1/2 h-full opacity-30 -translate-x-1/2 w-[2px] bg-[#196551]/40" />
                <div className="sticky top-[50%] after:h-[100vh] after:w-[2px] after:bg-[#196551] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0" />
                <div className="sticky  bg-[#CADFD9]/40 backdrop-blur-[2rem] w-[2.75rem] h-[2.75rem] top-[50%] -translate-x-[.2rem] flex items-center justify-center">
                  <div className="bg-[#196551] w-[1.21875rem] h-[1.21875rem]" />
                </div>
              </div>
            </div>

            <ul className="flex flex-col gap-[2rem]  w-[min(67.25rem,100%)] pl-[4rem] lg:pl-0">
              {data.introItems.map((item, i) => (
                <IntroItem key={`intro-item-${i}`} index={i}>
                  <div className="flex justify-between w-full">
                    <div className="border w-[8.5rem] h-[8.5rem] rounded-[0.375rem] border-[#001B14]/10 p-[0.625rem]">
                      <div className="flex justify-center items-center w-full h-full rounded-[0.3125rem] bg-[#001B14]/5">
                        <p className="pb-0 m-0 leading-none text-[#196551] font-blacker text-[4.5rem] -mb-[.5rem]">
                          0{i + 1}
                        </p>
                      </div>
                    </div>
                    <DotsIcon />
                  </div>
                  <Image
                    src={cardDots}
                    alt=""
                    className="absolute top-0 right-0 h-full mix-blend-exclusion pointer-events-none"
                  />
                  {item?.text && (
                    <div className="richtext richtext-start prose-p:text-[#001B14] prose-p:tracking-[-0.0825rem] prose-p:font-[300] prose-p:font-geist prose-p:text-[2.75rem] prose-strong:font-[500]">
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </IntroItem>
              ))}
            </ul>
          </div>
        )}

        {data?.introCTA && (
          <div className="lg:hidden">
            <CTAButton data={data.introCTA} />
          </div>
        )}
      </div>
    </section>
  );
}
